import { useCallback, useEffect, useRef, useState } from "react";

import { makeRequest } from "../../utils/request";

export function useAdminFetch<T>(url: string | null): { data: T | null; isLoading: boolean; error: string | null; refresh: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(url !== null);
  const [error, setError] = useState<string | null>(null);
  const [fetchCount, setFetchCount] = useState(0);
  const lastUrlRef = useRef<string | null>(url);

  useEffect(() => {
    if (!url) {
      lastUrlRef.current = url;
      setData(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    // Clear stale data when the url actually changes (but not on refresh(),
    // which reloads the same url and should not flash a loading state).
    if (lastUrlRef.current !== url) {
      lastUrlRef.current = url;
      setData(null);
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    makeRequest<T>(url)
      .then((response) => {
        if (cancelled) return;
        setData(response);
        setIsLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Request failed");
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, fetchCount]);

  const refresh = useCallback(() => setFetchCount((count) => count + 1), []);

  return { data, isLoading, error, refresh };
}
